import React, {useEffect, useImperativeHandle, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import {useActionItem, useActionItemAction} from 'helpers/hooks/action';
import Tooltip from 'components/atoms/Tooltips/Tooltip/Tooltip';
import Icon from 'components/atoms/Icons/Icon/Icon';
import StyledActionFab from 'components/molecules/Fabs/ActionFab/ActionFab.styles';
import DropdownPopper from 'components/molecules/Poppers/DropdownPopper/DropdownPopper';
import List from 'components/atoms/Lists/List/List';
import ListItem from 'components/atoms/Lists/ListItem/ListItem';
import utils from 'helpers/utils';

const ActionFab = React.forwardRef((props, ref) => {
  const {
    action,
    onClick,
    showInactive,
    autoOpen,
    IconProps,
    TooltipProps,
    DropDownPopperProps,
    ...innerProps
  } = useComponentProps({...props, ...props.action.ActionFabProps}, 'ActionFab', {
    static: ['open', 'closed']
  });

  const itm = useActionItem(action);
  const handleClick = useActionItemAction(itm?.onClick, onClick);

  const innerRef = useRef(null);
  useImperativeHandle(ref, () => innerRef.current);

  const [internalState, setInternalState] = useState({open: false, closed: true});

  const renderFab = (onClick) => {
    return <Tooltip title={action.tooltip} showDisabled={true}
                    fullWidth={innerProps.fullWidth ?? action.FabProps?.fullWidth}
                    placement={action.tooltipPosition ?? 'bottom'}
                    {...TooltipProps} {...action.TooltipProps}>
      <StyledActionFab ref={innerRef} {...innerProps}
                       disabled={!itm}
                       selected={itm?.active}
                       badge={action.badge}
                       onClick={onClick}
                       color={action.color ?? innerProps.color ?? 'default'}
                       {...action.FabProps}>
        {utils.isReactElement(action.icon) ? action.icon : <Icon icon={action.icon} {...IconProps}/>}
      </StyledActionFab>
    </Tooltip>
  }

  useEffect(() => {
    if (autoOpen) {
      setInternalState((current) => {
        return utils.updater({
          ...current,
          open: true,
          closed: false
        })(current);
      });
    }
  }, [autoOpen]);

  const handleFabClick = (e) => {
    setInternalState((current) => {
      return utils.updater({
        ...current,
        open: !current.open,
        closed: !current.open ? false : current.closed
      })(current);
    });
    e.preventDefault();
  }

  const handlePopperToggle = (open) => {
    setInternalState((current) => {
      return utils.updater({
        ...current,
        open: open,
        closed: open ? false : current.closed
      })(current);
    });
  }

  const handlePopperClosed = () => {
    setInternalState(utils.updater({closed: true}, true));
  }

  innerProps.className = utils.flattenClassName(innerProps.className, {
    open: internalState.open,
    closed: internalState.closed
  });

  if (!itm && !showInactive) {
    return <React.Fragment/>;
  } else {
    if (action.children || action.popper) {
      return <React.Fragment>
        {renderFab(handleFabClick)}
        <DropdownPopper anchorEl={innerRef.current}
                        open={internalState.open}
                        onToggle={handlePopperToggle}
                        onClosed={handlePopperClosed}
                        {...DropDownPopperProps}>
          {action.popper ? action.popper : <List track={true} gap={4}>
            {action.children.map((action, idx) => {
              return <ListItem key={idx}>
                <ActionFab action={action}
                           onClick={() => setInternalState(utils.updater({open: false}, true))}
                           fullWidth={true}/>
              </ListItem>
            })}
          </List>}
        </DropdownPopper>
      </React.Fragment>
    } else {
      return renderFab(handleClick);
    }
  }
});

ActionFab.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  action: PropTypes.object,
  onClick: PropTypes.func,
  showInactive: PropTypes.bool,
  autoOpen: PropTypes.bool,
  IconProps: PropTypes.object,
  TooltipProps: PropTypes.object,
  DropDownPopperProps: PropTypes.object
};

export default ActionFab;
